/**
 * Boîte de confirmation — réservée à ce qui **ne s'annule pas**.
 *
 * ── Pourquoi elle existe, alors que D§3 déclare les modales rares par principe ──
 *
 * Parce que D§3 a raison : demander « es-tu sûr ? » sur un geste réversible n'est qu'une
 * friction, et un jeu qui en met partout apprend au joueur à valider sans lire. Mais
 * **vendre un bâtiment ne se défait pas** et ne rend que la moitié du prix. C'est
 * exactement le cas que la règle réserve — et jusqu'ici un seul tap sur « Vendre »
 * détruisait le bâtiment sans un mot (audit § U9).
 *
 * ── Trois décisions d'implémentation ────────────────────────────────────────
 *
 * **Une seule modale, réutilisée.** `createModal` pose un écouteur `keydown` sur le
 * document : en fabriquer une par demande en accumulerait un par bâtiment vendu.
 *
 * **Le rappel est REMPLACÉ à chaque `ask`, jamais cumulé.** Brancher un nouvel
 * écouteur sur le bouton à chaque ouverture aurait vendu deux bâtiments au deuxième
 * clic — le genre de défaut qu'on ne voit qu'en vendant deux fois de suite.
 *
 * **Annuler passe en premier dans les actions.** `createModal` donne le focus au
 * premier élément focalisable : le choix pré-sélectionné doit être celui qui ne coûte
 * rien, sinon une confirmation destructrice se valide d'un « Entrée » réflexe.
 * Cet ordre n'est plus laissé à ce fichier — `appendDecision` le tient, et le marque
 * dans le DOM pour qu'il se vérifie sans lire un libellé traduit.
 */

import { createButton } from './components';
import { appendDecision, createModal } from './components/overlays';
import type { IconName } from './icons';

export interface ConfirmOptions {
  readonly title: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly icon?: IconName;
}

export interface ConfirmHandle {
  readonly el: HTMLElement;
  /** Pose la question. `onConfirm` n'est appelé que si le joueur confirme. */
  ask(text: string, onConfirm: () => void): void;
  /** Vrai tant que la question est à l'écran — l'appelant peut s'y adapter. */
  isOpen(): boolean;
  destroy(): void;
}

export function createConfirm(options: ConfirmOptions): ConfirmHandle {
  const modal = createModal({
    title: options.title,
    ...(options.icon === undefined ? {} : { icon: options.icon }),
    // Fermer au voile ou à Échap vaut ANNULER : la question reste sans réponse, et une
    // absence de réponse ne doit jamais valoir un oui.
    onClose: () => {
      pending = null;
    },
  });

  // Montée sur `body` : une boîte de dialogue couvre toute la page, et un parent en
  // `pointer-events: none` la rendrait inerte — le piège déjà rencontré deux fois.
  document.body.append(modal.el);

  const text = document.createElement('p');
  text.className = 'v-modal__text';
  modal.content.append(text);

  let pending: (() => void) | null = null;

  const cancel = createButton({
    label: options.cancelLabel,
    variant: 'ghost',
    onClick: () => {
      pending = null;
      modal.close();
    },
  });

  const confirm = createButton({
    label: options.confirmLabel,
    variant: 'primary',
    onClick: () => {
      const run = pending;
      pending = null;
      modal.close();
      run?.();
    },
  });

  /*
    ── DEUX CLASSES NOMMÉES, ET C'EST UNE DÉCISION ───────────────────────────

    Les deux boutons n'étaient distinguables que par leur ORDRE dans le DOM. C'est
    exactement la supposition que la règle 22 interdit : `.shop-actions .v-btn` en première
    position a désigné « Poser » jusqu'au jour où un sélecteur de quantité s'est glissé
    devant, et la sonde a tapé « ×1 » en boucle.

    Une confirmation est le pire endroit possible pour cette faute : se tromper de bouton
    ne rate pas une mesure, elle **confirme une action irréversible** qu'on croyait annuler.
    On nomme donc les deux, pour les sondes comme pour toute feuille de style à venir.
  */
  cancel.el.classList.add('v-modal__cancel');
  confirm.el.classList.add('v-modal__confirm');

  appendDecision(modal, { cancel: cancel.el, confirm: confirm.el });

  return {
    el: modal.el,
    ask(message, onConfirm) {
      text.textContent = message;
      pending = onConfirm;
      modal.open();
    },
    isOpen: () => modal.isOpen(),
    destroy() {
      pending = null;
      modal.destroy();
      modal.el.remove();
    },
  };
}
